'use client'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material'
import axios from 'axios'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState, useTransition } from 'react'
import Marquee from 'react-fast-marquee'
import io, { Socket } from 'socket.io-client'

import ProgressBarWhite from '@/components/ProgressBarWhite'
import { useRuangDialog } from '@/contexts/RuangDialogContext'
import { fetchRuang } from '@/libs/api/getRuang'
import showToastAlert from '@components/Toast'

const ViewPage = ({ urlAPI, unitList }: { urlAPI: string; unitList: any }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [deviceId, setDeviceId] = useState<string>('')
  const [ruangList, setRuangList] = useState<any[]>([])
  const [unit, setUnit] = useState<string>('')
  const [idRuangSocket, setIdRuangSocket] = useState<string>('')
  const [dataRuangS, setDataRuangS] = useState<any>(null)
  const [currentTime, setCurrentTime] = useState<Date>(new Date())

  const [showRuangDialog, setShowRuangDialog] = useState(false)
  const { open, openDialog, closeDialog } = useRuangDialog()
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const socketRef = useRef<Socket | null>(null)

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    getDeviceId()
    getRuangList()
  }, [])

  useEffect(() => {
    if (unit) {
      getDeviceId()
      getRuangList()
    }
  }, [unit])

  const getRuangList = async () => {
    const ruang = await fetchRuang(urlAPI)
    setRuangList(ruang)
  }

  const getDeviceId = async () => {
    try {
      let savedDeviceId = localStorage.getItem('deviceId')
      if (savedDeviceId) {
        setDeviceId(savedDeviceId)
        getData(savedDeviceId)
        return
      }
      const generatedId = await generateDeviceFingerprint()
      localStorage.setItem('deviceId', generatedId)
      setDeviceId(generatedId)
      getData(generatedId)
    } catch (error) {
      console.error('Error getting device ID:', error)
      const fallbackId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem('deviceId', fallbackId)
      setDeviceId(fallbackId)
      getData(fallbackId)
    }
  }

  const generateDeviceFingerprint = async (): Promise<string> => {
    const fingerprint = []
    fingerprint.push(`${screen.width}x${screen.height}`)
    fingerprint.push(Intl.DateTimeFormat().resolvedOptions().timeZone)
    fingerprint.push(navigator.language)
    fingerprint.push(navigator.platform)
    const ua = navigator.userAgent
    const browserInfo = ua.includes('Chrome')
      ? 'Chrome'
      : ua.includes('Firefox')
        ? 'Firefox'
        : ua.includes('Safari')
          ? 'Safari'
          : 'Other'
    fingerprint.push(browserInfo)
    fingerprint.push(navigator.hardwareConcurrency || 'unknown')
    if ('memory' in performance) {
      fingerprint.push((performance as any).memory.jsHeapSizeLimit || 'unknown')
    }
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.textBaseline = 'top'
        ctx.font = '14px Arial'
        ctx.fillText('Device fingerprint', 2, 2)
        fingerprint.push(canvas.toDataURL().slice(-50))
      }
    } catch (e) {
      fingerprint.push('canvas_unavailable')
    }
    const combined = fingerprint.join('|')
    const hash = await simpleHash(combined)
    return `TMC_${hash}`
  }

  const simpleHash = async (str: string): Promise<string> => {
    if (crypto && crypto.subtle) {
      try {
        const encoder = new TextEncoder()
        const data = encoder.encode(str)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        return hashArray
          .map(b => b.toString(16).padStart(2, '0'))
          .join('')
          .substr(0, 16)
      } catch (e) {
        return fallbackHash(str)
      }
    }
    return fallbackHash(str)
  }

  const fallbackHash = (str: string): string => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash
    }
    return Math.abs(hash).toString(36).substr(0, 16)
  }

  const getData = async (deviceIdentifier: string) => {
    setLoading(true)
    try {
      const response = await axios.get(urlAPI + 'ref/carikodedsiplay/' + deviceIdentifier)
      if (response.data.metadata.code === 200) {
        const fetchedData = response.data.response.list
        getDataReg(fetchedData[0].ID_RUANG, fetchedData[0].ID_UNIT)
      } else {
        openDialog()
      }
    } catch (error: any) {
      console.error('Error fetching data:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectRuang = async (ruang: any) => {
    setLoading(true)
    try {
      const payload = {
        idunit: unit,
        idruang: ruang.ID_RUANG,
        ip: deviceId
      }
      await axios.post(urlAPI + 'ref/updatedisplay', payload)
      closeDialog()
      setShowRuangDialog(false)
      getDataReg(ruang.ID_RUANG, unit)
      sessionStorage.setItem('dataRuang', JSON.stringify(ruang))
      showToastAlert('success', `Ruang "${ruang.NAMA_RUANG}" dipilih!`)
    } catch (error: any) {
      showToastAlert('error', error?.message || 'Gagal update display!')
    } finally {
      setLoading(false)
    }
  }

  const closseRuangDialog = () => {
    setShowRuangDialog(false)
  }

  const getDataReg = async (ID_RUANG: string, ID_UNIT: string) => {
    setLoading(true)
    try {
      const response = await axios.post(urlAPI + 'registrasi/cariregistrasruang', {
        idunit: ID_UNIT,
        idruang: ID_RUANG
      })
      if (response.data.metadata.code === 200) {
        const fetchedData = response.data.response.list[0]
        startTransition(() => {
          sessionStorage.setItem('dataReg', JSON.stringify(fetchedData))
          sessionStorage.setItem(
            'dataUnitSelect',
            JSON.stringify(unitList.find((u: any) => u.ID_UNIT === ID_UNIT))
          )
          const query = `?unit=${ID_UNIT}&noreg=` + fetchedData.NO_REG
          router.push(`/display${query}`)
        })
      }
    } catch (error: any) {
      console.error('Error login:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const dataRuangSession = sessionStorage.getItem('dataRuang')
    if (dataRuangSession) {
      setDataRuangS(JSON.parse(dataRuangSession))
    }
    socketRef.current = io(process.env.NEXT_PUBLIC_NEXT_SOCKET as string, {
      transports: ['websocket']
    })
    socketRef.current.on('connect', () => {
      socketRef.current?.emit('channel', 'ETERNAL-HOME-DISPLAY-RUANG')
    })
    socketRef.current.on('connect_error', err => {
      console.log('Connection error:', err.message)
    })
    socketRef.current.on('message', (data: any) => {
      if (data !== 'UPDATE-APP') {
        setIdRuangSocket(data)
      }
    })
    return () => {
      socketRef.current?.disconnect()
    }
  }, [])

  useEffect(() => {
    if (idRuangSocket === dataRuangS?.ID_RUANG) {
      setIdRuangSocket('')
      showToastAlert('success', 'Data baru masuk, silakan tunggu...')
      getDataReg(dataRuangS?.ID_RUANG, dataRuangS?.ID_UNIT)
    }
  }, [idRuangSocket])

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })

  const formatDate = (date: Date) =>
    date.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@300;400;600;700&display=swap');

        @keyframes floatLogo {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-16px); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes cardIn {
          from { opacity: 0; transform: translateY(40px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseRing {
          0%   { box-shadow: 0 0 0 0 rgba(var(--mui-palette-primary-mainChannel) / 0.35); }
          70%  { box-shadow: 0 0 0 20px rgba(var(--mui-palette-primary-mainChannel) / 0); }
          100% { box-shadow: 0 0 0 0 rgba(var(--mui-palette-primary-mainChannel) / 0); }
        }

        @keyframes shimmerBg {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }

        @keyframes clockBlink {
          0%, 49%  { opacity: 1; }
          50%, 100% { opacity: 0.2; }
        }

        @keyframes waveBar {
          0%,100% { transform: scaleY(0.4); }
          50%     { transform: scaleY(1); }
        }

        .home-logo {
          animation: floatLogo 4s ease-in-out infinite, pulseRing 3s ease-out infinite;
        }

        .home-fade-in {
          animation: fadeInUp 0.9s ease-out both;
        }

        .home-card-in {
          animation: cardIn 0.6s ease-out both;
        }

        .home-card-in:nth-child(1) { animation-delay: 0.05s; }
        .home-card-in:nth-child(2) { animation-delay: 0.12s; }
        .home-card-in:nth-child(3) { animation-delay: 0.19s; }
        .home-card-in:nth-child(4) { animation-delay: 0.26s; }
        .home-card-in:nth-child(5) { animation-delay: 0.33s; }
        .home-card-in:nth-child(6) { animation-delay: 0.40s; }

        .blink-colon {
          animation: clockBlink 1s step-start infinite;
        }

        .wave-bar {
          display: inline-block;
          width: 6px;
          border-radius: 4px;
          background: var(--mui-palette-primary-main);
          margin: 0 2px;
          animation: waveBar 1.2s ease-in-out infinite;
        }
        .wave-bar:nth-child(2) { animation-delay: 0.2s; }
        .wave-bar:nth-child(3) { animation-delay: 0.4s; }
        .wave-bar:nth-child(4) { animation-delay: 0.6s; }
        .wave-bar:nth-child(5) { animation-delay: 0.8s; }
      `}</style>

      {isPending && <ProgressBarWhite />}

      {/* ===== Dialog Pilih Unit ===== */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth={false}
        fullWidth
        transitionDuration={250}
        PaperProps={{
          sx: {
            width: '50vw',
            maxWidth: 'none',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.18)'
          }
        }}
      >
        {/* Dialog header strip */}
        <Box sx={{
          background: 'linear-gradient(135deg, var(--mui-palette-primary-main), var(--mui-palette-primary-dark))',
          py: 3,
          textAlign: 'center'
        }}>
          <Typography sx={{
            color: '#fff',
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            fontSize: { xs: '3vh', md: '4vh' },
            letterSpacing: 3
          }}>
            Pilih Unit
          </Typography>
        </Box>
        <DialogContent sx={{ py: 4, px: 5, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} justifyContent='center'>
            {unitList && unitList.length > 0 ? (
              unitList.map((u: any) => (
                <Grid item xs={12} key={u.ID_UNIT}>
                  <Button
                    variant='contained'
                    fullWidth
                    onClick={() => {
                      setUnit(u.ID_UNIT)
                      closeDialog()
                      setShowRuangDialog(true)
                    }}
                    sx={{
                      py: 2.5,
                      fontSize: '2.5vh',
                      fontWeight: 700,
                      letterSpacing: 2,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 28px rgba(0,0,0,0.2)'
                      },
                      transition: 'all 0.25s ease'
                    }}
                  >
                    {u.NAMA_UNIT}
                  </Button>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography align='center' color='text.secondary' sx={{ fontSize: '2.5vh' }}>
                  Tidak ada data unit.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* ===== Main Content — TV Locked, No Scroll ===== */}
      <Box
        sx={{
          height: '100%',
          width: '100%',
          bgcolor: '#f5f7fa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: '4vw',
          py: '2vh',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative background circles */}
        <Box sx={{
          position: 'absolute',
          top: '-10vw',
          right: '-10vw',
          width: '40vw',
          height: '40vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(var(--mui-palette-primary-mainChannel)/0.08) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-8vw',
          left: '-8vw',
          width: '35vw',
          height: '35vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(var(--mui-palette-primary-mainChannel)/0.06) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        {/* Clock + Date row */}
        <Box
          className='home-fade-in'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4vw',
            mb: '2vh'
          }}
        >
          {/* Wave bars */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '3vh', gap: '3px' }}>
            {[80, 50, 100, 60, 80].map((pct, i) => (
              <Box key={i} className='wave-bar' sx={{ height: `${pct}%` }} />
            ))}
          </Box>

          {/* Clock */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              fontSize: '6vh',
              letterSpacing: 6,
              color: 'primary.main',
              lineHeight: 1
            }}>
              {formatTime(currentTime).split('').map((ch, i) => (
                <span key={i} className={ch === ':' ? 'blink-colon' : ''}>{ch}</span>
              ))}
            </Typography>
            <Typography sx={{
              color: 'text.secondary',
              fontSize: '1.8vh',
              letterSpacing: 2,
              fontWeight: 400,
              mt: 0.5
            }}>
              {formatDate(currentTime)}
            </Typography>
          </Box>

          {/* Wave bars mirror */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '3vh', gap: '3px', transform: 'scaleX(-1)' }}>
            {[80, 50, 100, 60, 80].map((pct, i) => (
              <Box key={i} className='wave-bar' sx={{ height: `${pct}%`, animationDelay: `${i * 0.2}s` }} />
            ))}
          </Box>
        </Box>

        {/* Logo + Title block */}
        <Box
          className='home-fade-in'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: '2vh',
            animationDelay: '0.2s'
          }}
        >
          {/* Logo */}
          <Box
            className='home-logo'
            sx={{
              mb: '2vh',
              borderRadius: '50%',
              bgcolor: '#fff',
              p: '1.5vh',
              width: '14vh',
              height: '14vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
            }}
          >
            <img
              src='/images/icon/logo.png'
              alt='Logo TMC Eternal Home'
              style={{
                display: 'block',
                maxWidth: '80%',
                maxHeight: '80%',
                objectFit: 'contain'
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 900,
              fontSize: '7vh',
              letterSpacing: 8,
              textAlign: 'center',
              lineHeight: 1.1,
              color: 'primary.main',
              mb: '0.5vh'
            }}
          >
            TMC ETERNAL HOME
          </Typography>

          {/* Subtitle */}
          <Typography sx={{
            color: 'text.secondary',
            fontSize: '2vh',
            letterSpacing: 5,
            textTransform: 'uppercase',
            fontWeight: 300
          }}>
            Display System
          </Typography>

          {/* Divider */}
          <Box sx={{
            mt: '1.5vh',
            width: '80px',
            height: '4px',
            borderRadius: '2px',
            background: 'linear-gradient(to right, var(--mui-palette-primary-light), var(--mui-palette-primary-main), var(--mui-palette-primary-light))'
          }} />
        </Box>

        {/* Marquee ticker */}
        <Box
          className='home-fade-in'
          sx={{
            width: '100%',
            bgcolor: 'primary.main',
            borderRadius: '12px',
            py: '1.8vh',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            animationDelay: '0.4s',
            flexShrink: 0
          }}
        >
          <Marquee
            gradient={false}
            speed={90}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '3vh',
              fontWeight: 600,
              color: '#ffffff',
              letterSpacing: 8,
              textTransform: 'uppercase'
            }}
          >
            {'  ✦  SELAMAT DATANG DI TMC ETERNAL HOME  ✦  WELCOME TO OUR DISPLAY SYSTEM  ✦  TERIMA KASIH ATAS KUNJUNGAN ANDA  ✦  '}
          </Marquee>
        </Box>

        {/* ===== Dialog Pilih Ruangan ===== */}
        <Dialog
          open={showRuangDialog}
          onClose={closseRuangDialog}
          maxWidth={false}
          fullWidth
          transitionDuration={250}
          PaperProps={{
            sx: {
              width: '92vw',
              maxHeight: '88vh',
              maxWidth: 'none',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 32px 100px rgba(0,0,0,0.2)'
            }
          }}
        >
          {/* Dialog header */}
          <Box sx={{
            background: 'linear-gradient(135deg, var(--mui-palette-primary-main), var(--mui-palette-primary-dark))',
            py: { xs: 3, md: 4 },
            px: 4,
            textAlign: 'center'
          }}>
            <Typography sx={{
              color: '#fff',
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: { xs: '3.5vh', md: '5vh' },
              letterSpacing: 3,
              lineHeight: 1
            }}>
              Pilih Ruangan
            </Typography>
            <Typography sx={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: { xs: '1.8vh', md: '2.2vh' },
              mt: 1,
              letterSpacing: 1
            }}>
              Kode display tidak ditemukan — silakan pilih ruangan untuk melanjutkan
            </Typography>
          </Box>

          <DialogContent sx={{ bgcolor: '#f5f7fa', py: 4, px: { xs: 3, md: 5 } }}>
            <Grid container spacing={3} justifyContent='center'>
              {ruangList && ruangList.length > 0 ? (
                ruangList.map((ruang: any) => (
                  <Grid item xs={12} sm={6} md={4} key={ruang.ID_RUANG} className='home-card-in'>
                    <Card
                      elevation={0}
                      onClick={() => handleSelectRuang(ruang)}
                      sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: '20px',
                        cursor: 'pointer',
                        bgcolor: '#fff',
                        border: '2px solid transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minHeight: { xs: '180px', md: '220px' },
                        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        '&:hover': {
                          transform: 'translateY(-10px) scale(1.02)',
                          borderColor: 'primary.main',
                          boxShadow: '0 16px 50px rgba(0,0,0,0.14)'
                        }
                      }}
                    >
                      {/* ID badge */}
                      <Box sx={{
                        px: 2,
                        py: 0.6,
                        borderRadius: '8px',
                        bgcolor: 'primary.light',
                        mb: 2
                      }}>
                        <Typography sx={{
                          color: 'primary.contrastText',
                          fontSize: '1.8vh',
                          fontWeight: 600,
                          letterSpacing: 2
                        }}>
                          RUANG · {ruang.ID_RUANG}
                        </Typography>
                      </Box>

                      {/* Room name */}
                      <Typography sx={{
                        fontFamily: "'Playfair Display', serif",
                        fontWeight: 700,
                        fontSize: { xs: '3vh', md: '4vh' },
                        textAlign: 'center',
                        color: 'primary.main',
                        lineHeight: 1.2,
                        mb: 'auto',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {ruang.NAMA_RUANG}
                      </Typography>

                      {/* Divider */}
                      <Box sx={{
                        width: '40px',
                        height: '3px',
                        borderRadius: '2px',
                        bgcolor: 'primary.light',
                        my: 2
                      }} />

                      {/* Button */}
                      <Button
                        variant='contained'
                        size='large'
                        fullWidth
                        sx={{
                          borderRadius: '10px',
                          fontWeight: 700,
                          letterSpacing: 2,
                          fontSize: { xs: '1.8vh', md: '2.2vh' },
                          py: { xs: 1.5, md: 2 },
                          textTransform: 'none',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          '&:hover': {
                            boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
                            transform: 'scale(1.02)'
                          },
                          transition: 'all 0.25s ease'
                        }}
                      >
                        ✓ Pilih Ruangan
                      </Button>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant='body1' color='text.secondary' align='center' sx={{ fontSize: '2.5vh', py: 5 }}>
                    Tidak ada data ruangan tersedia.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
        </Dialog>
      </Box>
    </>
  )
}

export default ViewPage
